import { styled } from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import Menu from '@mui/material/Menu';
import Icon from '@assets/softplus/logo.svg?react';

interface MenuProps {
  $selected: boolean;
}

interface ControllersProps {
  expanded: boolean;
}

interface MenuItemProps {
  active?: boolean;
  expanded?: boolean;
}

const drawerWidth = '70vw';

export const Logo = styled(Icon)`
  width: 48px;
  height: 48px;
  color: ${({ theme }) => theme.palette.primary['01']};
`;

export const SidebarMenuItem = styled(Link)<MenuItemProps>`
  display: flex;
  width: 80%;
  align-items: flex-start;
  justify-content: center;
  gap: 10px;
  box-sizing: border-box;

  &[data-active='true'] {
    background-color: ${({ theme }) => theme.palette.primary['01']};
    color: ${({ theme }) => theme.palette.fixed.white};

    &:hover {
      color: ${({ theme }) => theme.palette.white['01']};
    }
  }

  &[data-expanded='true'] {
    justify-content: flex-start;
  }

  background-color: 'transparent';
  color: ${({ theme }) =>
    theme.palette.mode === 'dark'
      ? theme.palette.fixed.white
      : theme.palette.primary['01']};
  padding: 12px 12px;
  border-radius: 8px;

  &:hover {
    color: ${(props) =>
      props.active
        ? props.theme.palette.white['01']
        : props.theme.palette.mode === 'dark'
          ? props.theme.palette.primary['01']
          : props.theme.palette.secondary.main};
  }
`;

export const Container = styled('div')<ControllersProps>`
  display: flex;
  z-index: 3;
  flex-direction: column;
  align-items: ${({ expanded }) => (expanded ? 'flex-start' : 'center')};
  width: ${({ expanded }) => (expanded ? '240px' : '72px')};
  background-color: ${({ theme }) => theme.palette.white['01']};
  padding: 24px 0;
  height: 100vh;
  color: ${({ theme }) => theme.palette.grey['02']};
  transition: width 0.3s ease;
  justify-content: center;
`;

export const ButtonContainer = styled(Button)`
  height: 32px;
  width: 32px;
  min-width: 10px;
  color: ${({ theme }) => theme.palette.white['06']};
  justify-content: center;
  border-radius: 0;
  outline: none;

  &:hover {
    background-color: ${({ theme }) => theme.palette.white['03']};
  }
`;

export const SelectedButton = styled('div')`
  height: 32px;
  min-width: 10px;
  justify-content: center;
  background-color: ${({ theme }) => theme.palette.primary['02']};
  padding-left: 4px;
  color: ${({ theme }) => theme.palette.white['06']};
  outline: none;
  box-sizing: border-box;
`;

export const UserContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  cursor: pointer;
  white-space: nowrap;
`;

export const AvatarImage = styled('img')`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

export const Avatar = styled('div')<{ $bgColor: string; $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background-color: ${({ $bgColor }) => $bgColor};
  color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 18px;
  font-weight: 500;
`;

export const SubMenu = styled(Button)<MenuProps>`
  width: 100%;
  justify-content: flex-start;
  padding-left: 32px;
  color: ${({ theme, $selected }) =>
    $selected ? theme.palette.primary['04'] : theme.palette.primary['03']};
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.palette.primary['03'] : 'transparent'};

  &:hover {
    background-color: ${({ theme }) => theme.palette.grey['04']};

    color: ${({ theme }) => theme.palette.tertiary.main};
  }
`;

export const LogoClass = styled('div')`
  width: 48px;
  height: 48px;
  margin: 16px;
  color: ${({ theme }) => theme.palette.primary['01']};
`;

export const ControllersContainer = styled('div')<ControllersProps>`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: ${({ expanded }) => (expanded ? 'row' : 'column')};
  gap: 8px;
  color: ${(props) => props.theme.palette.white['06']};
`;

export const StyledMenu = styled(Menu)<MenuProps>`
  & .MuiPaper-root {
    border-radius: 6px;
    margin-top: ${({ theme }) => theme.spacing(1)};
    min-width: 200px;
    color: #ffffff;
    box-shadow:
      rgb(255, 255, 255) 0px 0px 0px 0px,
      rgba(0, 0, 0, 0.05) 0px 0px 0px 1px,
      rgba(0, 0, 0, 0.1) 0px 10px 15px -3px,
      rgba(0, 0, 0, 0.05) 0px 4px 6px -2px;
    background-color: ${({ theme }) => theme.palette.white['01']};
  }

  & .MuiMenu-list {
    padding: 8px 0;
  }

  & .MuiMenuItem-root {
    & .MuiSvgIcon-root {
      font-size: 18px;
      color: ${({ theme }) => theme.palette.text.primary};
      margin-right: ${({ theme }) => theme.spacing(1.5)};
    }

    &:active {
      background-color: ${({ theme }) => theme.palette.white['01']};
    }
    &:hover {
      color: ${({ theme }) => theme.palette.primary['01']};
    }
  }
`;

export const NavTypography = styled(Typography)`
  font-weight: 500;
  font-size: 0.875rem;
  color: currentColor;
`;

export const TopBar = styled(Toolbar)`
  display: flex;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.palette.white['01']};
`;

export const NavDrawer = styled(Drawer)`
  display: block;
  & .MuiDrawer-paper {
    width: drawerWidth;
    box-sizing: border-box;
    background-color: ${({ theme }) => theme.palette.white['01']};
  }
`;

export const DrawerStack = styled(Stack)`
  justify-content: space-between;
  width: ${drawerWidth};
  height: 100%;
`;

export const SubmenuList = styled(List)`
  background-color: ${({ theme }) => theme.palette.white['02']};
  padding-left: 16px;
  gap: 4px;
`;

export const MenuItemsStack = styled(Stack)`
  align-items: flex-start;
  display: flex;
  align-content: center;
  gap: 8px;
  width: 100%;
`;

export const MainItemsStack = styled(Stack)`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: ${({ expanded }) => (expanded ? 'column' : 'row')};
  padding-left: ${({ expanded }) => (expanded ? '12px' : '0px')};
`;

export const ListItem = styled(ListItemButton)`
  padding: 4px 8px;
  &:hover {
    background-color: ${({ theme }) => theme.palette.primary['03']};
    color: ${({ theme }) => theme.palette.fixed.white};
  }
`;
